import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import WorkflowCard from '../components/workflow/WorkflowCard';
import { Workflow, WorkflowStatus } from '../types/workflowTypes';
import { getWorkflows } from '../services/workflowService';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAuth } from '../context/AuthContext';
import { useIsReadOnly } from '../hooks/useIsReadOnly';

// Tatsächlich verwendete Workflow-Status für den Filter
type UsedWorkflowStatus = 'all' | 'draft' | 'sent' | 'accepted' | 'invoiced' | 'paid' | 'overdue' | 'rejected' | 'canceled';

const WorkflowManagementPage: React.FC = () => {
  const { t } = useTranslation('workflow');
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [filteredWorkflows, setFilteredWorkflows] = useState<Workflow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<UsedWorkflowStatus>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [sortField, setSortField] = useState<string>('updatedAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  
  // Pagination
  const [limit, setLimit] = useState<number>(20);
  const [offset, setOffset] = useState<number>(0);
  const [total, setTotal] = useState<number>(0);
  const [hasMore, setHasMore] = useState<boolean>(false);
  
  const navigate = useNavigate();
  const { currentUser, effectiveScopeUid } = useAuth();
  // M11b-3: advisor (read-only) darf keinen Workflow starten (= Angebot erstellen).
  const isReadOnly = useIsReadOnly();

  // Übersetzungen für Filter-Labels
  const getStatusFilterLabels = (): Record<UsedWorkflowStatus, string> => ({
    all: t('management.statusFilter.all'),
    draft: t('management.statusFilter.draft'),
    sent: t('management.statusFilter.sent'),
    accepted: t('management.statusFilter.accepted'),
    invoiced: t('management.statusFilter.invoiced'),
    paid: t('management.statusFilter.paid'),
    overdue: t('management.statusFilter.overdue'),
    rejected: t('management.statusFilter.rejected'),
    canceled: t('management.statusFilter.canceled')
  });

  // Unterstützte Sortierfelder laut API
  const getSortableFields = () => [
    { key: 'updatedAt', label: t('management.sort.updatedAt') },
    { key: 'createdAt', label: t('management.sort.createdAt') },
    { key: 'name', label: t('management.sort.name') },
    { key: 'status', label: t('management.sort.status') }
  ];

  // Hilfsfunktion für die lokale Sortierung der Workflows
  const sortWorkflows = (workflows: Workflow[], field: string, direction: 'asc' | 'desc'): Workflow[] => {
    return [...workflows].sort((a, b) => {
      let valueA: any;
      let valueB: any;

      switch (field) {
        case 'name':
          // Für Namen verwenden wir name, oder customer.companyName/personName
          valueA = a.name || a.customer?.companyName || a.customer?.personName || '';
          valueB = b.name || b.customer?.companyName || b.customer?.personName || '';
          break;
        case 'status':
          valueA = a.status || '';
          valueB = b.status || '';
          break;
        case 'createdAt':
          valueA = a.createdAt instanceof Date ? a.createdAt.getTime() : 
                  (a.createdAt ? new Date(a.createdAt).getTime() : 0);
          valueB = b.createdAt instanceof Date ? b.createdAt.getTime() : 
                  (b.createdAt ? new Date(b.createdAt).getTime() : 0);
          break;
        case 'updatedAt':
          valueA = a.updatedAt instanceof Date ? a.updatedAt.getTime() : 
                  (a.updatedAt ? new Date(a.updatedAt).getTime() : 0);
          valueB = b.updatedAt instanceof Date ? b.updatedAt.getTime() : 
                  (b.updatedAt ? new Date(b.updatedAt).getTime() : 0);
          break;
        default:
          // Fallback für andere Felder
          valueA = (a as any)[field] || '';
          valueB = (b as any)[field] || '';
      }

      // Strings vergleichen
      if (typeof valueA === 'string' && typeof valueB === 'string') {
        const comparison = valueA.localeCompare(valueB, 'de', { sensitivity: 'base' });
        return direction === 'asc' ? comparison : -comparison;
      }
      
      // Zahlen und Daten vergleichen
      if (valueA < valueB) return direction === 'asc' ? -1 : 1;
      if (valueA > valueB) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Laden der Workflows mit API-Parametern
  const loadWorkflows = async () => {
    try {
      setLoading(true);
      
      // API-Parameter basierend auf OpenAPI-Spezifikation
      const params: Record<string, any> = {
        limit,
        offset,
        sortBy: sortField,
        sortDirection
      };
      
      // Füge optionale Filter hinzu
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      
      const data = await getWorkflows(effectiveScopeUid, params);
      
      if (Array.isArray(data)) {
        setWorkflows(data);
        setFilteredWorkflows(data);
        
        // Setze Pagination-Infos
        let totalCount = data.length;
        let hasMorePages = false;
        
        // Prüfe, ob Pagination-Informationen im Response vorhanden sind
        if (data.length > 0 && data[0]?.pagination) {
          totalCount = data[0].pagination.total;
          hasMorePages = data[0].pagination.hasMore || data.length >= limit;
        } else {
          // Fallback-Berechnung, wenn keine Pagination-Info vorhanden ist
          hasMorePages = data.length >= limit;
        }
        
        setTotal(totalCount);
        setHasMore(hasMorePages);
              } else {
          setError(t('management.errors.unexpectedFormat'));
        }
      
      setLoading(false);
      setIsInitialLoading(false);
    } catch (err) {
      setError(t('management.errors.loadingError'));
      setLoading(false);
      setIsInitialLoading(false);
      console.error('Fehler beim Laden der Workflows:', err);
    }
  };

  // Initial und bei Änderung der Filter laden
  useEffect(() => {
    if (currentUser) {
      loadWorkflows();
    } else {
      setLoading(false);
      setError(t('management.errors.loginRequired'));
    }
  }, [currentUser, effectiveScopeUid, statusFilter, limit, offset, sortField, sortDirection]);

  // Sortierung umschalten
  const toggleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };
  
  // Sortiere Workflows lokal, wenn backend-sortierung nicht funktioniert
  useEffect(() => {
    if (workflows.length > 0) {
      // Lokale Sortierung anwenden
      const sorted = sortWorkflows(workflows, sortField, sortDirection);
      
      // Anwenden der Suchfilterung auf die sortierte Liste
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const filteredResults = sorted.filter(workflow => {
          // Hier die gleiche Suchlogik wie oben
          const name = (workflow.name || '').toLowerCase();
          const status = (workflow.status || '').toLowerCase();
          const type = (workflow.processType || workflow.type || '').toLowerCase();
          const customerId = workflow.customer?.id || '';
          const customerName = (workflow.customer?.companyName || workflow.customer?.personName || '').toLowerCase();
          
          // Suche in Dokumenten
          const documentsMatch = (workflow.documents || []).some(doc => {
            const docNumber = (doc.number || '').toLowerCase();
            const docType = (doc.type || '').toLowerCase();
            return docNumber.includes(query) || docType.includes(query);
          });
          
          // Suche in Metadaten
          const metadataMatch = workflow.metadata ? 
            Object.entries(workflow.metadata).some(([key, value]) => {
              if (typeof value === 'string') {
                return value.toLowerCase().includes(query);
              } else if (value && typeof value === 'object' && 'toString' in value) {
                return value.toString().toLowerCase().includes(query);
              }
              return false;
            }) : false;
          
          return name.includes(query) || 
                status.includes(query) || 
                type.includes(query) || 
                customerId.includes(query) ||
                customerName.includes(query) ||
                documentsMatch ||
                metadataMatch;
        });
        
        setFilteredWorkflows(filteredResults);
      } else {
        setFilteredWorkflows(sorted);
      }
    }
  }, [sortField, sortDirection, workflows, searchQuery]);
  
  // Neue Seite laden
  const loadNextPage = () => {
    setOffset(prev => prev + limit);
  };
  
  const loadPrevPage = () => {
    setOffset(prev => Math.max(0, prev - limit));
  };
  
  // Neuen Workflow durch Erstellen eines Angebots beginnen
  const handleCreateWorkflow = () => {
    navigate('/dashboard/angebote');
  };

  return (
    <div className="bg-surface text-primary min-h-screen">
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {/* Header mit Titel und Action-Button */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
            <h1 className="text-2xl font-bold">{t('management.title')}</h1>
            {/* Workflow-Start (= Angebot erstellen) — M11b-3: für advisor (read-only) ausgeblendet */}
            {!isReadOnly && (
            <button
              onClick={handleCreateWorkflow}
              className="bg-indigo-600 hover:bg-indigo-700 text-[#0C1322] font-semibold py-2 px-4 rounded-lg transition duration-150 ease-in-out"
            >
              {t('management.createNewOffer')}
            </button>
            )}
          </div>
          
          {/* Filter-Bereich */}
          <div className="bg-surface-raised rounded-xl p-6 mb-6 border border-hairline">
            <div className="flex justify-between items-center mb-4">
                              <h2 className="text-xl font-semibold text-primary">{t('management.filterAndSort')}</h2>
              <button 
                onClick={() => {
                  setStatusFilter('all');
                  setSearchQuery('');
                  setSortField('updatedAt');
                  setSortDirection('desc');
                }}
                className="text-accent-ink hover:text-accent-ink font-medium text-sm"
              >
                {t('management.resetFilters')}
              </button>
            </div>
            
            <div className="flex flex-col md:flex-row gap-4">
              {/* Status-Filter */}
              <div className="md:w-1/3">
                                  <label htmlFor="statusFilter" className="block text-sm font-medium text-secondary mb-1">
                    {t('management.status')}
                </label>
                <select
                  id="statusFilter"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as UsedWorkflowStatus)}
                  className="block w-full rounded-md bg-surface-inset border-strong text-primary py-2 px-3 focus:border-indigo-500 focus:ring-indigo-500"
                >
                  {Object.entries(getStatusFilterLabels()).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Suchfeld */}
              <div className="md:w-2/3">
                                  <label htmlFor="searchQuery" className="block text-sm font-medium text-secondary mb-1">
                    {t('management.search')}
                </label>
                <input
                  id="searchQuery"
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                                      placeholder={t('management.searchPlaceholder')}
                  className="block w-full rounded-md bg-surface-inset border-strong text-primary py-2 px-3 focus:border-indigo-500 focus:ring-indigo-500"
                />
              </div>
            </div>
            
            {/* Sortierbuttons */}
            <div className="flex mt-4 gap-2 flex-wrap">
              {getSortableFields().map(field => (
                <button
                  key={field.key}
                  onClick={() => toggleSort(field.key)}
                  className={`px-3 py-1 text-sm rounded-md ${
                    sortField === field.key ? 'bg-indigo-600 text-[#0C1322]' : 'bg-surface-inset text-secondary hover:bg-surface-hover'
                  } transition duration-150 ease-in-out`}
                >
                  {field.label} {sortField === field.key && (sortDirection === 'asc' ? '↑' : '↓')}
                </button>
              ))}
            </div>
          </div>
          
          {/* Fehlermeldung */}
          {error && (
            <div className="bg-[var(--tone-danger-bg)] border border-[var(--tone-danger-border)] text-[var(--tone-danger-fg)] px-4 py-3 rounded-md mb-4">
              <p>{error}</p>
            </div>
          )}
          
          {/* Hauptinhalt */}
          {loading && !isInitialLoading ? (
            <div className="flex justify-center my-12">
              <LoadingSpinner />
            </div>
          ) : isInitialLoading ? (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              {Array.from({ length: 6 }).map((_, index) => (
                <div key={index} className="bg-surface-raised shadow-lg rounded-xl p-5 border border-hairline h-full animate-pulse">
                  <div className="flex justify-between items-start mb-3">
                    <div className="w-3/4">
                      <div className="h-5 bg-surface-inset rounded mb-2"></div>
                      <div className="h-4 bg-surface-inset rounded w-1/2"></div>
                    </div>
                    <div className="h-6 bg-surface-inset rounded-full w-20"></div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="h-12 bg-surface-inset rounded"></div>
                    <div className="h-12 bg-surface-inset rounded"></div>
                    <div className="h-12 bg-surface-inset rounded"></div>
                    <div className="h-12 bg-surface-inset rounded"></div>
                  </div>
                  <div className="h-20 bg-surface-inset rounded mb-4"></div>
                  <div className="h-10 bg-surface-inset rounded"></div>
                </div>
              ))}
            </div>
          ) : filteredWorkflows.length === 0 ? (
            <div className="bg-surface-raised rounded-xl p-8 text-center border border-hairline">
                              <h3 className="text-xl font-medium text-secondary mb-2">{t('management.noWorkflowsFound')}</h3>
              <p className="text-muted mb-6">
                                  {statusFilter !== 'all' || searchQuery.trim()
                    ? t('management.adjustFilters')
                    : t('management.createFirstWorkflow')}
              </p>
              {(statusFilter !== 'all' || searchQuery.trim()) && (
                <button
                  onClick={() => {
                    setStatusFilter('all');
                    setSearchQuery('');
                  }}
                  className="text-accent-ink hover:text-accent-ink font-medium"
                >
                  {t('management.resetAllFilters')}
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                {filteredWorkflows.map((workflow) => (
                  <WorkflowCard key={workflow.id} workflow={workflow} />
                ))}
              </div>
              
              {/* Pagination */}
              {(hasMore || offset > 0) && (
                <div className="flex justify-between items-center mt-6 bg-surface-raised rounded-lg p-3 border border-hairline">
                  <span className="text-sm text-muted">
                                          {t('management.results', { 
                        start: offset + 1, 
                        end: Math.min(offset + filteredWorkflows.length, total), 
                        total 
                      })}
                  </span>
                  <div className="flex gap-2">
                    <button
                      onClick={loadPrevPage}
                      disabled={offset === 0}
                      className={`px-3 py-1 rounded text-sm ${
                        offset === 0 
                          ? 'bg-surface-inset text-faint cursor-not-allowed' 
                          : 'bg-surface-inset text-primary hover:bg-surface-hover'
                      }`}
                    >
                                              {t('management.pagination.previous')}
                    </button>
                    <button
                      onClick={loadNextPage}
                      disabled={!hasMore}
                      className={`px-3 py-1 rounded text-sm ${
                        !hasMore 
                          ? 'bg-surface-inset text-faint cursor-not-allowed' 
                          : 'bg-indigo-600 text-[#0C1322] hover:bg-indigo-700'
                      }`}
                    >
                      {t('management.pagination.next')}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkflowManagementPage; 